/**
 * Hook de geolocalización para uso en campo.
 * - Lectura puntual (no watchPosition) para no drenar batería.
 * - Alta precisión activada por defecto (GPS del dispositivo).
 * - Estado reactivo (loading, error, lastFix) para usar desde formularios.
 * @module hooks/useGeolocation
 */

import { useCallback, useState } from 'react'

export interface GeoFix {
  lat: number
  lng: number
  accuracy: number // metros
  altitude?: number | null
  heading?: number | null
  speed?: number | null
  timestamp: number
}

export interface UseGeolocationResult {
  getCurrentPosition: (opts?: {
    highAccuracy?: boolean
    timeoutMs?: number
    maxAgeMs?: number
  }) => Promise<GeoFix>
  loading: boolean
  error: string | null
  lastFix: GeoFix | null
  supported: boolean
  clear: () => void
}

const DEFAULT_TIMEOUT_MS = 15_000
const DEFAULT_MAX_AGE_MS = 0

function mapError(err: GeolocationPositionError): string {
  switch (err.code) {
    case err.PERMISSION_DENIED:
      return 'Permiso de ubicación denegado. Habilítalo en los ajustes del navegador para usar el GPS.'
    case err.POSITION_UNAVAILABLE:
      return 'No se pudo obtener tu ubicación. Verifica que el GPS esté activo y que tengas cielo abierto.'
    case err.TIMEOUT:
      return 'La solicitud de ubicación tardó demasiado. Intenta de nuevo en un espacio abierto.'
    default:
      return err.message || 'Error desconocido obteniendo ubicación.'
  }
}

export function useGeolocation(): UseGeolocationResult {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastFix, setLastFix] = useState<GeoFix | null>(null)

  const supported = typeof navigator !== 'undefined' && !!navigator.geolocation

  const getCurrentPosition = useCallback(
    (opts?: { highAccuracy?: boolean; timeoutMs?: number; maxAgeMs?: number }) => {
      return new Promise<GeoFix>((resolve, reject) => {
        if (!supported) {
          const msg = 'Este dispositivo no soporta geolocalización.'
          setError(msg)
          reject(new Error(msg))
          return
        }

        setLoading(true)
        setError(null)

        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const fix: GeoFix = {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
              altitude: pos.coords.altitude ?? null,
              heading: pos.coords.heading ?? null,
              speed: pos.coords.speed ?? null,
              timestamp: pos.timestamp,
            }
            setLastFix(fix)
            setLoading(false)
            resolve(fix)
          },
          (err) => {
            const msg = mapError(err)
            setError(msg)
            setLoading(false)
            reject(new Error(msg))
          },
          {
            enableHighAccuracy: opts?.highAccuracy ?? true,
            timeout: opts?.timeoutMs ?? DEFAULT_TIMEOUT_MS,
            maximumAge: opts?.maxAgeMs ?? DEFAULT_MAX_AGE_MS,
          }
        )
      })
    },
    [supported]
  )

  const clear = useCallback(() => {
    setError(null)
    setLastFix(null)
  }, [])

  return { getCurrentPosition, loading, error, lastFix, supported, clear }
}
